//React and Router  Hooks
import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// MUI components
import { Box, Tooltip, CircularProgress } from "@mui/material";

// MUI icons
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";

// Custom components
import CommonAccordion from "../../custom-components/CommonAccordion";
import CommonTable from "../../custom-components/CommonTestCasesTable";
import CommonDownload from "../../custom-components/CommonDownload";
import CommonCopy from "../../custom-components/CommonCopy";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import {
  MultipleTestCasesContext,
  TestCasesLoaderContext,
} from "../../main-pages/MultiTestCaseGenerationPage";
import { generateTestTechniques } from "../../../services/webServices";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import MessagePopup from "../../popup-components/MessagePopup";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";

const MultipleTestConditionsComponent = () => {
  //useContext
  const { setLoadingState } = useContext(LoadingModalContext);
  const { testCaseLoaderState, setTestCaseLoaderState } = useContext(
    TestCasesLoaderContext
  );
  const {
    testConditions,
    functionalSpecDocument,
    testingTechniques,
    setTestingTechniques,
  } = useContext(MultipleTestCasesContext);

  //for navigation
  const navigate = useNavigate();
  const location = useLocation();

  //state variables
  const [error, setError] = useState(null);
  const [activeThumb, setActiveThumb] = useState(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  //handle like and dislike
  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  //open downlaod dialog
  const handleDownload = () => {
    setDownloadOpen(true);
  };

  // transform the testConditions into flat data
  const transformedTestConditions = (testConditions || [])
    .filter((obj) => obj && typeof obj === "object") // Filter out null/undefined objects
    .flatMap((obj) =>
      Object.entries(obj)
        .filter(([key]) => key !== "Risk-prioritized scenarios")
        .flatMap(([requirement, conditions]) =>
          (Array.isArray(conditions) ? conditions : [conditions]).map(
            (condition) => ({
              Requirement: requirement,
              "Test condition": condition,
            })
          )
        )
    );

  // Then, use it to initialize the state
  const [testConditionsResponse, setTestConditionsResponse] = useState(
    transformedTestConditions
  );

  //accordion opening condition
  const defaultTestConditionExpanded =
    testConditionsResponse &&
    testConditionsResponse.length > 0 &&
    Object.entries(testingTechniques).length === 0;

  //for word and pdf
  const handleTestConditionsTableData = (testConditionsData) => {
    if (!Array.isArray(testConditionsData)) {
      console.error(
        "Invalid testConditionsData (expected array)",
        testConditionsData
      );
      return { headers: [], values: [] };
    }
    const headers = ["Requirement", "Test condition"];
    const values = [];
    testConditionsData
      .filter((obj) => obj && typeof obj === "object") // Filter out null/undefined objects
      .forEach((obj) => {
        Object.entries(obj).forEach(([key, value]) => {
          if (key !== "Risk-prioritized scenarios") {
            const conditions = Array.isArray(value)
              ? value
              : [String(value || "")];
            conditions.forEach((condition) => {
              values.push([key, condition]);
            });
          }
        });
      });
    return { headers, values };
  };

  //generate test techniques
  const handleGenerateTestTechniques = async () => {
    let progressHandler;
    try {
      setError(null);

      // Check if testConditions is empty or undefined
      if (!testConditions || testConditions.length === 0) {
        setPopupMessage(
          "No test conditions available. Please generate test conditions first."
        );
        setPopupOpen(true);
        return;
      }

      setTestCaseLoaderState("Test Techniques");
      setTestingTechniques([]);
      progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 30000,
      });
      progressHandler.start("Generating Test Techniques");

      const techniquePromises = testConditions
        .filter((obj) => obj && typeof obj === "object") // Filter out null/undefined objects
        .map(async (eachTestCondition, index) => {
          if (eachTestCondition && Object.keys(eachTestCondition).length > 0) {
            const requestData = {
              functionalSpecDocument:
                functionalSpecDocument["Functional Requirements"],
              testConditions: eachTestCondition,
            };
            try {
              const testTechniques = await generateTestTechniques(requestData);
              if (
                testTechniques?.data &&
                typeof testTechniques.data === "object"
              ) {
                return testTechniques.data;
              } else {
                console.warn(
                  `No data returned for test condition #${index + 1}`
                );
                return null;
              }
            } catch (innerError) {
              console.error(
                `Error for test condition ${index + 1}:`,
                innerError
              );
              return null;
            }
          }
          return null;
        });
      const allTechniqueResponses = await Promise.all(techniquePromises);
      const testTechniquesObject = {};
      allTechniqueResponses.forEach((response) => {
        if (response) {
          for (const [key, value] of Object.entries(response)) {
            if (!testTechniquesObject[key]) {
              testTechniquesObject[key] = [];
            }
            testTechniquesObject[key].push(value);
          }
        }
      });

      if (Object.keys(testTechniquesObject).length === 0) {
        setPopupMessage("No test techniques were generated. Please try again.");
        setPopupOpen(true);
        return;
      }
      await progressHandler.finishAndClose();
      navigate("/dashboard/generate-multi/testcases/output", {
        state: {
          ...location.state,
          testingTechniques: testTechniquesObject,
        },
        replace: true,
      });
    } catch (error) {
      console.error("Global error:", error);
      const errorMsg =
        error?.response?.data?.Error ||
        "Something went wrong while generating test techniques.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      setError("Error generating test techniques: " + error.message);
    } finally {
      setTestCaseLoaderState(null);
      if (progressHandler) {
        progressHandler.stop();
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  //for rendering inside accordion
  const testConditionsDetails = (
    <Box sx={{ height: "100%" }}>
      <Box
        sx={{
          height: "100%",
        }}
      >
        <CommonTable
          edittable={true}
          headers={["Requirement", "Test condition"]}
          values={testConditionsResponse.map((obj) => [
            obj.Requirement,
            obj["Test condition"],
          ])}
          tableData={testConditionsResponse}
          setTableData={setTestConditionsResponse}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginTop: 7,
          marginBottom: 2,
        }}
      >
        <Tooltip title="Like" arrow>
          <Box
            onClick={() => handleThumbClick("like")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": {
                color: "#49A3FF",
              },
            }}
          >
            {activeThumb === "like" ? (
              <ThumbUpIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>

        <Tooltip title="Dislike" arrow>
          <Box
            onClick={() => handleThumbClick("dislike")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": {
                color: "#FF4842",
              },
            }}
          >
            {activeThumb === "dislike" ? (
              <ThumbDownIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>
        <CommonCopy contentToCopy={testConditionsResponse} />
      </Box>
    </Box>
  );

  return (
    <Box>
      <CommonAccordion
        summary={"Test Conditions"}
        details={testConditionsDetails}
        type={"output"}
        defaultExpanded={defaultTestConditionExpanded}
        buttons={[
          {
            label: "Download",
            variant: "text",
            onClick: handleDownload,
            startIcon: <FileDownloadOutlinedIcon />,
            sx: { textTransform: "none" },
          },
          {
            label: "Find Test Techniques",
            onClick: handleGenerateTestTechniques,
            variant: "contained",
            disabled: testCaseLoaderState === "Test Techniques",
          },
        ]}
        fullScreenMode={true}
      />
      <CommonDownload
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        serviceName={"Test Conditions"}
        serviceResponse={[
          {
            heading: "Test Conditions",
            type: "table",
            data: handleTestConditionsTableData(testConditionsResponse),
          },
        ]}
        wordData={[
          {
            heading: "Test Conditions",
            type: "table",
            data: handleTestConditionsTableData(testConditionsResponse),
          },
        ]}
        documents={["docx", "pdf"] || []}
      />
      <MessagePopup
        modalOpen={popupOpen}
        okayHandler={() => setPopupOpen(false)}
        message={popupMessage}
      />
    </Box>
  );
};

export default MultipleTestConditionsComponent;
